import React from 'react';
import { Modal, Grid, theme } from 'antd';

const { useBreakpoint } = Grid;

/**
 * ResponsiveModal - A reusable wrapper around Ant Design's Modal component
 * that automatically applies mobile-responsive styling with visual section separators.
 * 
 * Features:
 * - Fullscreen (90% width) on mobile, customizable width on desktop
 * - Visual separators: borderBottom on header, borderTop on footer (mobile only)
 * - Sticky footer on mobile with elevated background
 * - Theme-aware using Ant Design's design tokens
 * - Supports all standard Ant Design Modal props
 * 
 * @param {number|string} desktopWidth - Width of modal on desktop (default: 520)
 * @param {object} ...modalProps - All other Ant Design Modal props
 */
const ResponsiveModal = ({
    desktopWidth = 520,
    children,
    styles: customStyles = {},
    footer,
    ...modalProps
}) => {
    const screens = useBreakpoint();
    const { token } = theme.useToken();
    const isMobile = screens.xs;

    // Smart footer detection:
    // - If footer is explicitly set (custom or null), respect that
    // - If footer is undefined, check if onOk/onCancel exist (default footer will show)
    const hasFooter = footer === undefined
        ? (modalProps.onOk !== undefined || modalProps.onCancel !== undefined)
        : (footer !== null);

    // Merge custom styles with responsive defaults
    const responsiveStyles = {
        header: {
            padding: '12px 16px',
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            ...customStyles.header
        },
        body: {
            padding: isMobile ? '12px 16px' : 24,
            maxHeight: isMobile ? (hasFooter ? 'calc(100dvh - 152px)' : 'calc(100dvh - 100px)') : 'calc(80vh - 120px)',
            overflowY: 'auto',
            background: token.colorBgContainer,
            ...customStyles.body
        },
        // Only apply footer styles if footer is not null
        ...(hasFooter && {
            footer: {
                position: isMobile ? 'sticky' : 'relative',
                bottom: 0,
                zIndex: 1000,
                paddingTop: 10,
                paddingBottom: isMobile ? 0 : 10,
                backgroundColor: token.colorBgElevated,
                borderTop: isMobile ? `1px solid ${token.colorBorderSecondary}` : 'none',
                ...customStyles.footer
            }
        }),
        ...customStyles
    };

    return (
        <Modal
            {...modalProps}
            footer={footer}
            width={isMobile ? '90%' : desktopWidth}
            styles={responsiveStyles}
            centered={!isMobile}
            className="responsive-modal"
        >
            {children}
        </Modal>
    );
};

export default ResponsiveModal;
